/**
 * 2 · Carico magazzino — la baia.
 *
 * La cosa che questa schermata deve far capire in tre secondi è che **il
 * carico è un atto separato dalla ricezione** (regola 12): le cassette marcate
 * pronte al banco stanno in coda, e finché non si scansionano in baia e non si
 * conferma, per Modula non esistono.
 *
 * Sotto le cassette c'è il **messaggio vero**, coi nomi di campo del protocollo
 * Job 1.13. Serve a rendere discutibile l'integrazione e non solo l'interfaccia:
 * è la parte che il fornitore Modula deve poter leggere e correggere.
 */

import { Notifiche } from '../components/notifications';
import { Sezione } from '../components/section';
import { Stato } from '../components/state';
import { Spazio, Testata, Voce } from '../components/header';
import { Avviso, useFlusso } from '../flow-api';

interface Cassetta {
  codice: string;
  ordine: string;
  cliente: string;
  sku: string;
  colore: string;
  capi: number;
  /** I reparti da attraversare. Vuoto = neutro: salta la produzione. */
  pers?: string[];
  /** Merce a scorta: in magazzino si ferma, non va a fine linea. */
  aMagazzino?: boolean;
  /** Quando il banco l'ha consegnata, in ISO. */
  consegnataIl?: string;
}
/** Dove il magazzino ha messo una cassetta. Assente = non l'ha ancora detto. */
interface Dove {
  codice: string;
  posto?: { macchina: string; udc: number; scomparto: number };
}

interface VistaCarico {
  inAttesa: Cassetta[];
  inBaia: Cassetta[];
  stoccate: number;
  dove?: Dove[];
  missioni: { tipo: string; cassette: string[] }[];
}

/**
 * Dove va questa cassetta, in una riga.
 *
 * Tre destini e non due: la merce a scorta è neutra come un articolo non
 * decorato — nessuna personalizzazione — ma le due neutre si comportano al
 * contrario. Quella d'ordine attraversa il magazzino e va a fine linea da
 * spedire, questa **si ferma** finché un ordine futuro non la preleva.
 */
function destino(c: Cassetta): string {
  if (c.aMagazzino === true) return 'A SCORTA · resta in magazzino';
  const pers = c.pers ?? [];
  return pers.length === 0 ? 'NEUTRO · a fine linea' : pers.join(' → ');
}

/** La data come si legge in reparto: giorno e mese, senza l'anno. */
function quando(iso: string): string {
  const d = new Date(iso);
  const due = (n: number) => String(n).padStart(2, '0');
  return `${due(d.getDate())}/${due(d.getMonth() + 1)}`;
}

export function Magazzino() {
  const { dati, caricando, ultimo, inCorso, fai } = useFlusso<VistaCarico>('carico', '/carico');
  if (caricando || !dati) return <Stato quale="caricando" dice="Apro la baia…" />;

  return (
    <div className="shell">
      <Testata>
        <Voce k="Baia di carico" v={`${dati.inBaia.length} scansionate`} />
        <Voce k="In coda" v={dati.inAttesa.length} />
        <Voce k="A magazzino" v={dati.stoccate} />
        <Spazio />
        <button
          className="btn btn--primario"
          disabled={inCorso || dati.inBaia.length === 0}
          onClick={fai('/carico/conferma')}
        >
          Conferma inserimento
        </button>
        <Notifiche />
      </Testata>

      <div className="main">
        <section className="booth" style={{ background: 'var(--panel)' }}>
          <Sezione titolo="In baia adesso">
            {dati.inBaia.length === 0 ? (
              <p className="booth__vuoto">
                Scansiona le cassette che stai caricando. Finché non confermi, per Modula non
                esistono.
              </p>
            ) : (
              <div className="cassette">
                {dati.inBaia.map((c) => (
                  <button
                    key={c.codice}
                    className="cassetta cassetta--aperta"
                    disabled={inCorso}
                    onClick={fai('/carico/baia/togli', { cassetta: c.codice })}
                  >
                    <span className="cassetta__cod">{c.codice}</span>
                    <span className="cassetta__n">{c.capi} capi · togli</span>
                  </button>
                ))}
              </div>
            )}

            {/*
              Un'anteprima di com'è fatta la missione, non la missione: a
              comporla davvero è `corpoVersamento` in `@errebi/modula`, e a
              mandarla è l'API. Il `,000` qui sotto è ricopiato da
              `decimaleModula` di proposito — importarlo vorrebbe dire mettere
              il client del WMS nelle dipendenze del banco, che col WMS non
              parla. Se il formato del protocollo cambia, questo riquadro
              racconta una cosa vecchia: è un cartello, e va riletto.
            */}
            <div className="gesto" style={{ marginTop: 16 }}>
              <div className="gesto__top">
                <span className="gesto__k">Missione verso Modula</span>
              </div>
              <div className="gesto__body">
                <pre
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 12,
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {`CFG-IMP-ORDINI
IMP_ORDINI       ORD_TIPOOP = "V"        (versamento)
IMP_ORDINI_RIGHE RIG_HOSTINF = ${dati.inBaia[0]?.codice ?? '<cassetta>'}
                 RIG_ARTICOLO = ${dati.inBaia[0]?.sku ?? '<sku>'}
                 RIG_QTAR = ${(dati.inBaia[0]?.capi ?? 0).toFixed(3).replace('.', ',')}`}
                </pre>
                <p className="mod__hint">
                  Macchina, UDC e scomparto <b>non li indichiamo noi</b>: li sceglie il WMS. È la
                  cosa che più facilmente si dà per scontata al contrario.
                </p>
                {dati.missioni.length > 0 && (
                  <p className="mod__hint">
                    {dati.missioni.length} missioni già mandate in questa sessione.
                  </p>
                )}
              </div>
            </div>
          </Sezione>
        </section>

        <section className="righe">
          {/*
            Dove è finita la merce che abbiamo affidato al magazzino.

            Una cassetta accettata dal WMS non è ancora dentro: il posto lo
            sceglie lui e lo dice **dopo**, con l'esito. Finché non l'ha detto
            qui si legge «non ha ancora detto dove», che è un'informazione e non
            un buco — e distingue la merce riposta da quella ancora su una baia.
          */}
          {dati.dove !== undefined && dati.dove.length > 0 && (
            <Sezione
              titolo="Dove sono finite"
              azioni={
                <button className="mod" disabled={inCorso} onClick={fai('/carico/esiti')}>
                  Aggiorna dal WMS
                </button>
              }
            >
              <ul className="dove">
                {dati.dove.map((d) => (
                  <li key={d.codice}>
                    <span className="dove__cod">{d.codice}</span>
                    {d.posto ? (
                      <span className="dove__posto">
                        {d.posto.macchina} · UDC {String(d.posto.udc).padStart(4, '0')} · scomparto{' '}
                        {d.posto.scomparto}
                      </span>
                    ) : (
                      <span className="dove__attesa">il WMS non ha ancora detto dove</span>
                    )}
                  </li>
                ))}
              </ul>
            </Sezione>
          )}

          <Sezione titolo="Pronte, in attesa di essere caricate" distanziata>
            {/* «Niente in coda» con dati veri si legge come un guasto: la coda
                vuota è lo stato normale di metà giornata, e quel che serve
                sapere è da dove arrivano le cassette. */}
            {dati.inAttesa.length === 0 && (
              <p className="mod__hint">Nessuna cassetta è stata marcata pronta al banco.</p>
            )}
            {dati.inAttesa.map((c) => (
              <article key={c.codice} className="riga riga--att">
                <div className="riga__cliente">{c.cliente}</div>
                <div className="riga__sku">
                  {c.codice} · {c.sku} {c.colore} · ordine {c.ordine}
                </div>
                {/* Dove va, prima ancora di toccarla: la merce a scorta si ferma
                    in magazzino, la neutra salta la produzione, le altre hanno
                    un reparto. Sono tre destini diversi, e distinguerli qui
                    evita di portare a fine linea capi che nessuno può spedire. */}
                <div className="riga__pers">{destino(c)}</div>
                <div className="riga__conto">
                  {c.capi} capi
                  {c.consegnataIl !== undefined && ` · dal ${quando(c.consegnataIl)}`}
                </div>
                <button
                  className="mod"
                  style={{ marginTop: 8 }}
                  disabled={inCorso}
                  onClick={fai('/carico/baia', { cassetta: c.codice })}
                >
                  Scansiona in baia
                </button>
              </article>
            ))}
          </Sezione>
        </section>
      </div>

      <Avviso esito={ultimo} />
    </div>
  );
}
